/**
 * Черновик конструктора образа (UX-аудит F01).
 *
 * Пользователь накапливает результат работы шагами: выбранные вещи, текущий шаг, имя
 * образа. До этой правки любой уход со страницы (клик по совместимой вещи, перезагрузка,
 * браузерная «Назад») стирал всё без предупреждения. Черновик живёт в `sessionStorage`
 * текущей вкладки и привязан к гардеробу и редактируемому образу: чужой состав после
 * переключения гардероба не подхватывается.
 */

import {StepKey} from './steps';

/** Версия схемы: несовместимый черновик игнорируется и стирается. */
export const DRAFT_VERSION = 1;

export const DRAFT_STORAGE_KEY = 'clothes.outfit-builder.draft';

/** Вещь черновика — ровно то, что нужно для отрисовки и сохранения образа. */
export interface DraftItem {
    id: string;
    name: string;
    brandName: string | null;
    categoryId?: string | null;
    categoryName: string | null;
    firstPhotoUrl: string | null;
    rating: number | null;
}

export interface BuilderDraft<TWearState = unknown> {
    version: number;
    /** Гардероб, в котором собирался образ; `null` — свой. */
    ownerId: string | null;
    /** Редактируемый образ; `null` — новый. */
    outfitId: string | null;
    step: number;
    selections: Record<StepKey, DraftItem[]>;
    outfitName: string;
    wearStates: Record<string, TWearState | null>;
    savedAt: number;
}

/** Черновик пуст: восстанавливать нечего (и незачем показывать «Начать заново»). */
export function isEmptyDraft(draft: BuilderDraft): boolean {
    return Object.values(draft.selections).every(items => items.length === 0)
        && draft.outfitName.trim() === '';
}

function storage(): Storage | null {
    try {
        return window.sessionStorage;
    } catch {
        // Приватный режим или заблокированные данные сайта — конструктор работает без черновика.
        return null;
    }
}

/**
 * Читает черновик текущего контекста. Несовпадение гардероба или редактируемого образа —
 * не наш черновик: он стирается, чтобы не всплыть позже.
 */
export function loadDraft<TWearState>(
    ownerId: string | null,
    outfitId: string | null,
): BuilderDraft<TWearState> | null {
    const store = storage();
    if (!store) {
        return null;
    }
    let parsed: BuilderDraft<TWearState> | null = null;
    try {
        const raw = store.getItem(DRAFT_STORAGE_KEY);
        parsed = raw ? JSON.parse(raw) as BuilderDraft<TWearState> : null;
    } catch {
        // Битый JSON или заблокированное хранилище — черновика просто нет.
        parsed = null;
    }
    if (!parsed) {
        return null;
    }
    const matches = !!parsed
        && parsed.version === DRAFT_VERSION
        && !!parsed.selections
        && (parsed.ownerId ?? null) === ownerId
        && (parsed.outfitId ?? null) === outfitId;
    if (!matches) {
        clearDraft();
        return null;
    }
    return parsed;
}

export function saveDraft<TWearState>(draft: Omit<BuilderDraft<TWearState>, 'version' | 'savedAt'>): void {
    const store = storage();
    if (!store) {
        return;
    }
    try {
        store.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
            ...draft,
            version: DRAFT_VERSION,
            savedAt: Date.now(),
        }));
    } catch {
        // Квота исчерпана — черновик просто не сохранится, конструктор остаётся рабочим.
    }
}

export function clearDraft(): void {
    const store = storage();
    if (!store) {
        return;
    }
    try {
        store.removeItem(DRAFT_STORAGE_KEY);
    } catch {
        // Хранилище недоступно — стирать нечего.
    }
}
